import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, TextInput, View } from 'react-native';

import { Text } from '@/components/ui';
import { fetchAdminLlmCalls, fetchAdminLlmUsage } from '@/lib/admin/llm-models-api';
import {
  featureLabel,
  formatCount,
  formatDayLabel,
  formatUsd,
  type AdminLlmModel,
  type LlmCallRow,
  type LlmUsageSummary,
} from '@/lib/admin/llm-models-types';

const RANGES = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
] as const;

const FEATURES = [
  { id: '', label: 'All features' },
  { id: 'chat', label: 'Chat' },
  { id: 'finance_import', label: 'Finance import' },
  { id: 'embeddings', label: 'Embeddings' },
  { id: 'search', label: 'Search' },
] as const;

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function rangeFor(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
  return { from: isoDay(from), to: isoDay(to) };
}

const tableStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 5.5rem 8rem 5.5rem',
  columnGap: 16,
  rowGap: 10,
  alignItems: 'baseline',
} as const;

function TableHead() {
  return (
    <>
      <Text variant="caption" className="text-muted">
        {' '}
      </Text>
      <Text variant="caption" className="text-muted" style={{ textAlign: 'right' }}>
        Calls
      </Text>
      <Text variant="caption" className="text-muted" style={{ textAlign: 'right' }}>
        Tokens
      </Text>
      <Text variant="caption" className="text-muted" style={{ textAlign: 'right' }}>
        Cost
      </Text>
    </>
  );
}

function TableRow({
  label,
  calls,
  tokens,
  cost,
}: {
  label: string;
  calls: number;
  tokens: number;
  cost: number;
}) {
  return (
    <>
      <Text variant="body-sm" style={{ overflowWrap: 'anywhere' }}>
        {label}
      </Text>
      <Text variant="body-sm" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {formatCount(calls)}
      </Text>
      <Text variant="body-sm" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {formatCount(tokens)}
      </Text>
      <Text variant="body-sm" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {formatUsd(cost)}
      </Text>
    </>
  );
}

function BucketTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; label: string; calls: number; tokens: number; cost: number }>;
}) {
  return (
    <View className="rounded-lg border border-border p-4">
      <Text variant="h3">{title}</Text>
      {rows.length === 0 ? (
        <Text variant="caption" className="mt-2 text-muted">
          No calls in this range.
        </Text>
      ) : (
        <View className="mt-3" style={{ ...tableStyle, overflowX: 'auto' }}>
          <TableHead />
          {rows.map((row) => (
            <TableRow
              key={row.key}
              label={row.label}
              calls={row.calls}
              tokens={row.tokens}
              cost={row.cost}
            />
          ))}
        </View>
      )}
    </View>
  );
}

function modelLabel(modelId: string, models: AdminLlmModel[]): string {
  return models.find((model) => model.openrouterModelId === modelId)?.displayName ?? modelId;
}

export function LlmControlUsage({
  models,
  onError,
}: {
  models: AdminLlmModel[];
  onError: (message: string | null) => void;
}) {
  const [days, setDays] = useState<(typeof RANGES)[number]['days']>(30);
  const [summary, setSummary] = useState<LlmUsageSummary | null>(null);
  const [calls, setCalls] = useState<LlmCallRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [feature, setFeature] = useState('');
  const [status, setStatus] = useState('');
  const [modelInput, setModelInput] = useState('');
  const [model, setModel] = useState('');
  const [memberId, setMemberId] = useState('');
  const [sort, setSort] = useState<'createdAt' | 'cost'>('createdAt');
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setModel(modelInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [modelInput]);

  useEffect(() => {
    const range = rangeFor(days);
    let cancelled = false;
    setLoading(true);
    onError(null);
    void fetchAdminLlmUsage(range)
      .then((usage) => {
        if (!cancelled) setSummary(usage.summary);
      })
      .catch((err) => {
        if (!cancelled) onError(err instanceof Error ? err.message : 'Failed to load usage');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, onError]);

  useEffect(() => {
    const range = rangeFor(days);
    let cancelled = false;
    void fetchAdminLlmCalls({
      ...range,
      feature: feature || undefined,
      model: model || undefined,
      memberId: memberId || undefined,
      status: status || undefined,
      sort,
      dir: 'desc',
      limit: 50,
      offset,
    })
      .then((page) => {
        if (cancelled) return;
        setTotal(page.total);
        setCalls((current) => (offset === 0 ? page.calls : [...current, ...page.calls]));
      })
      .catch((err) => {
        if (!cancelled) onError(err instanceof Error ? err.message : 'Failed to load calls');
      });
    return () => {
      cancelled = true;
    };
  }, [days, feature, model, memberId, status, sort, offset, onError]);

  const totals = summary?.totals;

  return (
    <View className="mt-6 flex flex-col gap-4">
      <View className="flex-row flex-wrap gap-2">
        {RANGES.map((range) => (
          <Pressable
            key={range.id}
            onPress={() => {
              setOffset(0);
              setDays(range.days);
            }}
          >
            <Text variant="body-sm" className={days === range.days ? 'text-accent' : 'text-muted'}>
              {range.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {loading || !summary || !totals ? (
        <ActivityIndicator className="mt-4" />
      ) : (
        <>
          <View className="flex-row flex-wrap gap-3">
            <SummaryStat label="Cost" value={formatUsd(totals.cost)} />
            <SummaryStat label="Calls" value={formatCount(totals.calls)} />
            <SummaryStat
              label="Tokens"
              value={formatCount(totals.promptTokens + totals.completionTokens)}
            />
          </View>
          <Text variant="caption" className="text-muted">
            Successful calls only. Older chat turns and finance batches were copied in without
            latency. Prompt and response text is not stored.
          </Text>
          <BucketTable
            title="By day"
            rows={summary.byDay.map((row) => ({
              key: row.day,
              label: formatDayLabel(row.day),
              calls: row.calls,
              tokens: row.promptTokens + row.completionTokens,
              cost: row.cost,
            }))}
          />
          <BucketTable
            title="By feature"
            rows={summary.byFeature.map((row) => ({
              key: row.feature,
              label: featureLabel(row.feature),
              calls: row.calls,
              tokens: row.promptTokens + row.completionTokens,
              cost: row.cost,
            }))}
          />
          <BucketTable
            title="By model"
            rows={summary.byModel.map((row) => ({
              key: row.model,
              label: modelLabel(row.model, models),
              calls: row.calls,
              tokens: row.promptTokens + row.completionTokens,
              cost: row.cost,
            }))}
          />
          <BucketTable
            title="By member"
            rows={summary.byMember.map((row) => ({
              key: row.memberId ?? 'unknown',
              label: row.memberName ?? 'Unknown member',
              calls: row.calls,
              tokens: row.promptTokens + row.completionTokens,
              cost: row.cost,
            }))}
          />
        </>
      )}

      <View className="rounded-lg border border-border p-4">
        <Text variant="h3">Calls</Text>
        <Text variant="caption" className="mt-1 text-muted">
          {total} matching {total === 1 ? 'call' : 'calls'}
        </Text>
        <View className="mt-3 flex-row flex-wrap gap-3">
          {FEATURES.map((item) => (
            <Pressable
              key={item.id || 'all-features'}
              onPress={() => {
                setOffset(0);
                setFeature(item.id);
              }}
            >
              <Text
                variant="caption"
                className={feature === item.id ? 'text-accent' : 'text-muted'}
              >
                {item.label}
              </Text>
            </Pressable>
          ))}
        </View>
        <View className="mt-2 flex-row flex-wrap gap-3">
          {[
            { id: '', label: 'Any status' },
            { id: 'ok', label: 'Succeeded' },
            { id: 'error', label: 'Failed' },
          ].map((item) => (
            <Pressable
              key={item.id || 'all-status'}
              onPress={() => {
                setOffset(0);
                setStatus(item.id);
              }}
            >
              <Text variant="caption" className={status === item.id ? 'text-accent' : 'text-muted'}>
                {item.label}
              </Text>
            </Pressable>
          ))}
          <Pressable
            onPress={() => {
              setOffset(0);
              setSort((current) => (current === 'cost' ? 'createdAt' : 'cost'));
            }}
          >
            <Text variant="caption" className="text-accent">
              Sort by {sort === 'cost' ? 'cost' : 'time'}
            </Text>
          </Pressable>
        </View>
        <TextInput
          autoCapitalize="none"
          className="mt-3 w-full rounded-lg border border-border px-3 py-2 text-base"
          placeholder="Filter by model id"
          value={modelInput}
          onChangeText={(value) => {
            setOffset(0);
            setModelInput(value);
          }}
        />
        {summary && summary.byMember.some((row) => row.memberId) ? (
          <View className="mt-2 flex-row flex-wrap gap-3">
            <Pressable
              onPress={() => {
                setOffset(0);
                setMemberId('');
              }}
            >
              <Text variant="caption" className={memberId === '' ? 'text-accent' : 'text-muted'}>
                All members
              </Text>
            </Pressable>
            {summary.byMember
              .filter((row) => row.memberId)
              .map((row) => (
                <Pressable
                  key={row.memberId ?? 'unknown'}
                  onPress={() => {
                    setOffset(0);
                    setMemberId(row.memberId ?? '');
                  }}
                >
                  <Text
                    variant="caption"
                    className={memberId === (row.memberId ?? '') ? 'text-accent' : 'text-muted'}
                  >
                    {row.memberName ?? 'Unknown member'}
                  </Text>
                </Pressable>
              ))}
          </View>
        ) : null}
        {calls.map((call) => {
          const open = openId === call.id;
          return (
            <Pressable
              key={call.id}
              className="mt-3 border-t border-border pt-3"
              onPress={() => setOpenId(open ? null : call.id)}
            >
              <Text variant="body-sm">
                {new Date(call.createdAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </Text>
              <Text variant="body-sm" className="mt-1" style={{ overflowWrap: 'anywhere' }}>
                {featureLabel(call.feature)} · {modelLabel(call.model, models)}
              </Text>
              <Text variant="caption" className="mt-1 text-muted">
                {call.status === 'ok' ? 'Succeeded' : 'Failed'} · {formatCount(call.promptTokens)}{' '}
                in · {formatCount(call.completionTokens)} out · {formatUsd(call.cost)}
                {call.latencyMs != null ? ` · ${formatCount(call.latencyMs)} ms` : ''} ·{' '}
                {call.memberName ?? 'No member'}
              </Text>
              {open ? (
                <Text variant="caption" className="mt-2 text-muted">
                  {[
                    call.operation,
                    call.profile ? `profile ${call.profile}` : null,
                    call.model,
                    call.conversationId ? `conversation ${call.conversationId}` : null,
                    call.financeBatchId ? `batch ${call.financeBatchId}` : null,
                    call.messageId ? `message ${call.messageId}` : null,
                    call.openrouterGenerationId
                      ? `generation ${call.openrouterGenerationId}`
                      : null,
                    call.error,
                  ]
                    .filter(Boolean)
                    .join('\n')}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
        {calls.length < total ? (
          <Pressable className="mt-3" onPress={() => setOffset((current) => current + 50)}>
            <Text variant="caption" className="text-accent">
              Load more
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <View className="min-w-[8rem] flex-1 rounded-lg border border-border p-3">
      <Text variant="caption" className="text-muted">
        {label}
      </Text>
      <Text variant="h3" className="mt-1" style={{ whiteSpace: 'nowrap' }}>
        {value}
      </Text>
    </View>
  );
}
